import { languageQuery } from '@gig/magic-common';
import { FEATURE_QUERY_VALUE, FEATURE_QUERY_ENABLED, FEATURE_QUERY_ACTIVE, FEATURE_UPDATED, LANGUAGE_UPDATED } from '@gig/magic-messages';
import { __awaiter, __generator } from 'tslib';
import { magic } from '@gig/magic-sdk';

/**
 * The feature service handles checking if a feature is enabled
 * or disabled.
 *
 * The service **must** be initialized before usage.
 */
var FeatureService = /** @class */ (function () {
    function FeatureService(countryService, deviceService) {
        var _this = this;
        this.countryService = countryService;
        this.deviceService = deviceService;
        this.registerEvents();
        Promise.all([languageQuery(), this.getFeatures()]).then(function (_a) {
            var language = _a[0];
            _this.language = language;
            _this.registerQueryHandlers();
        });
        window.eventBus.subscribe(LANGUAGE_UPDATED, function (event) {
            _this.language = event.payload;
        });
    }
    FeatureService.prototype.registerQueryHandlers = function () {
        var _this = this;
        window.queryBus.subscribe(FEATURE_QUERY_VALUE, function (event) { return __awaiter(_this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                if (!((_a = event.payload) === null || _a === void 0 ? void 0 : _a.slug)) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, this.getFeatureValue(event.payload)];
            });
        }); });
        window.queryBus.subscribe(FEATURE_QUERY_ENABLED, function (event) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (!event.payload) {
                    return [2 /*return*/, false];
                }
                return [2 /*return*/, this.isFeatureEnabled(event.payload)];
            });
        }); });
        window.queryBus.subscribe(FEATURE_QUERY_ACTIVE, function (event) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (!event.payload) {
                    return [2 /*return*/, false];
                }
                return [2 /*return*/, this.isFeatureActive(event.payload)];
            });
        }); });
    };
    FeatureService.prototype.checkFeatureOverride = function () {
        var _this = this;
        var overrideValues = window.eventBus.getLatest(FEATURE_UPDATED);
        if (overrideValues) {
            this.overrideFeaturesFromQuery(overrideValues);
        }
        window.eventBus.subscribe(FEATURE_UPDATED, function (e) {
            if (e.payload && _this.features) {
                _this.overrideFeaturesFromQuery(e.payload);
            }
        });
    };
    FeatureService.prototype.registerEvents = function () {
        window.queryBus.register(FEATURE_QUERY_VALUE, {
            type: 'object',
            required: ['slug'],
            properties: {
                slug: {
                    type: 'string',
                },
                defaultValue: {
                    type: 'any',
                },
            },
        });
        window.queryBus.register(FEATURE_QUERY_ENABLED, {
            type: 'string',
        });
        window.eventBus.register(FEATURE_UPDATED, {
            type: 'object',
        });
    };
    FeatureService.prototype.getFeatures = function () {
        return __awaiter(this, void 0, void 0, function () {
            var features, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, magic.methods.sitefeatures.fetch()];
                    case 1:
                        features = _a.sent();
                        this.features = this.normalizeFeatures(features);
                        this.checkFeatureOverride();
                        return [2 /*return*/, this.features];
                    case 2:
                        e_1 = _a.sent();
                        console.error(e_1);
                        return [2 /*return*/, {}];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    FeatureService.prototype.normalizeFeatures = function (features) {
        return features.reduce(function (acc, feature) {
            acc[feature.slug] = feature;
            return acc;
        }, {});
    };
    FeatureService.prototype.overrideFeaturesFromQuery = function (queries) {
        var _this = this;
        Object.keys(queries).forEach(function (key) {
            if (_this.features[key]) {
                _this.features[key].active = queries[key].active;
                if (queries[key].feature) {
                    _this.features[key].feature = queries[key].feature;
                }
            }
        });
    };
    /**
     * Checks if the feature slug passed as a string is enabled or not.
     * @param slug The feature slug
     */
    FeatureService.prototype.isFeatureEnabled = function (slug) {
        if (!this.features) {
            return false;
        }
        var currentFeature = this.features[slug];
        return !!currentFeature && this.getFeatureValue({ slug: slug }).isApplicable;
    };
    /**
     * Gets the precise value of the feature and checks if it's currently
     * applicable.
     * @param slug The feature slug
     * @param config Current browser configuration, eg. ipCountry/desktop device etc
     * @param defaultValue The default value to return if the feature slug just doesn't exist.
     */
    FeatureService.prototype.getFeatureValue = function (_a) {
        var slug = _a.slug, _b = _a.defaultValue, defaultValue = _b === void 0 ? '' : _b, location = _a.location;
        if (!this.features) {
            return {
                isApplicable: false,
                value: null,
                location: [],
            };
        }
        var currentFeature = this.features[slug];
        if (!currentFeature) {
            return { isApplicable: false, value: defaultValue, location: [] };
        }
        return {
            isApplicable: this.isApplicable(currentFeature, {
                language: this.language,
                isDesktop: this.deviceService.isDesktop(),
                ipCountry: location ||
                    this.countryService.getCookieCountry(),
                user: magic.session.getUser(),
            }),
            value: currentFeature.feature,
            location: currentFeature.location,
        };
    };
    /**
     * Returns a boolean value if the feature that has been passed is actually valid
     * for the current logged in user/country/language.
     * @param feature The feature to check if is currently applicable
     * @param config demographics data
     */
    FeatureService.prototype.isApplicable = function (feature, config) {
        var currLang = config.language;
        var user = config.user;
        var currCountry = config.ipCountry;
        var isDesktop = config.isDesktop;
        if (!feature.active) {
            return false;
        }
        if (feature.languages.length > 0 &&
            !feature.languages.includes(currLang)) {
            return false;
        }
        if (!feature.platform.desktop && isDesktop) {
            return false;
        }
        if (!feature.platform.mobile && !isDesktop) {
            return false;
        }
        if (user) {
            if (feature.countriesExcludes.includes(user.country)) {
                return false;
            }
            if (feature.countries.length > 0 &&
                !feature.countries.includes(user.country)) {
                return false;
            }
        }
        if (feature.locationExcludes.includes(currCountry)) {
            return false;
        }
        if (feature.location.length > 0 &&
            !feature.location.includes(currCountry)) {
            return false;
        }
        if (user) {
            if (feature.depositCount !== null &&
                user.depositCount < feature.depositCount) {
                return false;
            }
            if (feature.affiliates.length > 0 &&
                !feature.affiliates.includes(user.affiliate || '')) {
                return false;
            }
            if (feature.affiliatesExcludes.includes(user.affiliate || '')) {
                return false;
            }
            if (feature.roles.length > 0 &&
                !feature.roles.every(function (role) { return user.roles.includes(role); })) {
                return false;
            }
            if (feature.rolesExcludes.some(function (role) { return user.roles.includes(role); })) {
                return false;
            }
            if (feature.segments.length > 0 &&
                !feature.segments.includes(user.segment)) {
                return false;
            }
        }
        return true;
    };
    /**
     * Checks if the feature slug passed as a string is active(enabled) or not.
     * @param slug The feature slug
     */
    FeatureService.prototype.isFeatureActive = function (slug) {
        if (!this.features) {
            return false;
        }
        var currentFeature = this.features[slug];
        return !!currentFeature && currentFeature.active;
    };
    return FeatureService;
}());

export { FeatureService };
